import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    'contentType',
    'bodyField',
    'singeAssetFields',
    'mainImageFields',
    'richBodyField',
    'twitterField',
    'webPageFields',
    'indoorMapField',
    'outDoorMapField',
    'customFormFields',
    'beforeAfterRichFields',
    'beforeAfterFields',
    'carouselSliderFields',
    'assetTypeLabel',
    'selectAssetButton',
    'replaceAssetButton',
    'removeAssetButton',
    'richBodyOptional',
    'indoorMapIdField',
    'outdoorMapIdField',
    'allowShareCheckbox',
    'leaderboardFields',
    'myPointsFields',
    'leaderboardWindow',
    'leaderboardCustomFields'
  ]

  IMAGE_TYPE = 'Image'
  VIDEO_TYPE = 'Video'
  AUDIO_TYPE = 'Audio'
  FILE_TYPE = 'File'
  FORMATTED_TEXT_TYPE = 'Formatted text'
  TWITTER_TYPE = 'Twitter'
  WEB_PAGE_TYPE = 'Web page'
  INDOOR_MAP_TYPE = 'Indoor map'
  OUTDOOR_MAP_TYPE = 'Outdoor map'
  CUSTOM_FORM_TYPE = 'Custom form'
  BEFORE_AFTER_TYPE = 'Before and After'
  SLIDESHOW_TYPE = 'Slideshow'
  LEADERBOARD_TYPE = 'Leaderboard'
  MY_POINTS_TYPE = 'My Points'

  SHARE_EXCLUDED_TYPES = ['Indoor map', 'Outdoor map', 'Custom form', 'Leaderboard', 'My Points']

  ALL_FIELDS = [
    'bodyField',
    'singeAssetFields',
    'mainImageFields',
    'richBodyField',
    'twitterField',
    'webPageFields',
    'outDoorMapField',
    'indoorMapField',
    'customFormFields',
    'beforeAfterRichFields',
    'carouselSliderFields',
    'beforeAfterFields',
    'leaderboardFields',
    'myPointsFields'
  ]

  VISIBLE_FIELDS_CONFIG = {
    [this.IMAGE_TYPE]: ['bodyField', 'singeAssetFields'],
    [this.VIDEO_TYPE]: ['bodyField', 'singeAssetFields'],
    [this.AUDIO_TYPE]: ['singeAssetFields', 'mainImageFields', 'richBodyField'],
    [this.FILE_TYPE]: ['bodyField', 'singeAssetFields', 'mainImageFields'],
    [this.FORMATTED_TEXT_TYPE]: ['richBodyField'],
    [this.TWITTER_TYPE]: ['bodyField', 'twitterField'],
    [this.WEB_PAGE_TYPE]: ['webPageFields'],
    [this.INDOOR_MAP_TYPE]: ['indoorMapField'],
    [this.OUTDOOR_MAP_TYPE]: ['outDoorMapField'],
    [this.CUSTOM_FORM_TYPE]: ['mainImageFields', 'richBodyField', 'customFormFields'],
    [this.BEFORE_AFTER_TYPE]: ['beforeAfterRichFields', 'beforeAfterFields'],
    [this.SLIDESHOW_TYPE]: ['carouselSliderFields', 'beforeAfterRichFields'],
    [this.LEADERBOARD_TYPE]: ['leaderboardFields', 'richBodyField'],
    [this.MY_POINTS_TYPE]: ['myPointsFields', 'richBodyField']
  }

  HELP_BLOCK_CONFIG = {
    [this.IMAGE_TYPE]: 'image',
    [this.VIDEO_TYPE]: 'video',
    [this.AUDIO_TYPE]: 'audio',
    [this.FORMATTED_TEXT_TYPE]: 'formatted_text',
    [this.TWITTER_TYPE]: 'twitter',
    [this.WEB_PAGE_TYPE]: 'web_page',
    [this.INDOOR_MAP_TYPE]: 'indoor_map',
    [this.OUTDOOR_MAP_TYPE]: 'outdoor_map',
    [this.CUSTOM_FORM_TYPE]: 'custom_form',
    [this.BEFORE_AFTER_TYPE]: 'before_after',
    [this.SLIDESHOW_TYPE]: 'slideshow',
    [this.LEADERBOARD_TYPE]: 'leaderboard',
    [this.MY_POINTS_TYPE]: 'my_points'
  }

  connect() {
    this.setContentFields()
  }

  changeContentType() {
    const contentType = this.contentTypeTarget.value
    const visibleFields = this.VISIBLE_FIELDS_CONFIG[contentType] || []

    this.setContentFields()
    this.clearIdFields(visibleFields)
  }

  setContentFields() {
    const contentType = this.contentTypeTarget.value
    const visibleFields = this.VISIBLE_FIELDS_CONFIG[contentType] || []

    this.toggleHelpBlocks(contentType)
    this.toggleFieldGroups(visibleFields)
    this.updateAssetAttributes(contentType)
    this.toggleRichBodyOptional(contentType)
    this.toggleAllowShare(contentType)
    this.toggleLeaderboardCustomFields()
  }

  toggleLeaderboardCustomFields() {
    if (!this.hasLeaderboardWindowTarget) return

    this.leaderboardCustomFieldsTarget.classList.toggle('hidden', this.leaderboardWindowTarget.value !== 'custom')
  }

  toggleFieldGroups(visibleFields) {
    this.ALL_FIELDS.forEach(fieldName => {
      const isVisible = visibleFields.includes(fieldName)
      const singularTarget = `${fieldName}Target`
      const pluralTargets = `${fieldName}Targets`

      if (this[pluralTargets].length > 1) {
        this[pluralTargets].forEach(element => {
          element.classList.toggle('hidden', !isVisible)
        })
      } else if (this[singularTarget]) {
        this[singularTarget].classList.toggle('hidden', !isVisible)
      }
    })

    // Prevent duplicate asset_attachment params: disable inputs in the inactive section
    ;['carouselSliderFields', 'beforeAfterFields'].forEach(fieldName => {
      const isVisible = visibleFields.includes(fieldName)
      const hasTargetKey = `has${fieldName[0].toUpperCase()}${fieldName.slice(1)}Target`
      if (this[hasTargetKey]) {
        this[`${fieldName}Target`].querySelectorAll('input[name*="asset_attachments"]').forEach(input => {
          input.disabled = !isVisible
        })
      }
    })
  }

  toggleHelpBlocks(contentType) {
    const activeHelpBlock = this.HELP_BLOCK_CONFIG[contentType]
    document.querySelectorAll('[data-asset-help]').forEach(helpBlock => {
      const helpType = helpBlock.getAttribute('data-asset-help')
      helpBlock.classList.toggle('hidden', helpType !== activeHelpBlock)
    })
  }

  toggleRichBodyOptional(contentType) {
    if (contentType === this.FORMATTED_TEXT_TYPE) {
      this.richBodyOptionalTarget.classList.add('hidden')
    } else {
      this.richBodyOptionalTarget.classList.remove('hidden')
    }
  }

  toggleAllowShare(contentType) {
    if (this.SHARE_EXCLUDED_TYPES.includes(contentType)) {
      this.allowShareCheckboxTarget.checked = false
      this.allowShareCheckboxTarget.disabled = true
    } else {
      this.allowShareCheckboxTarget.disabled = false
      this.allowShareCheckboxTarget.checked = true
    }
  }

  updateAssetAttributes(contentType) {
    this.assetTypeLabelTarget.textContent = contentType
    this.selectAssetButtonTarget.textContent = `Select ${contentType}`
    this.selectAssetButtonTarget.dataset.mediaLibraryMediaTypeParam = contentType
    this.replaceAssetButtonTarget.dataset.mediaLibraryMediaTypeParam = contentType
  }

  clearIdFields(visibleFields) {
    const mediaLibraryElement = this.singeAssetFieldsTarget
    const mediaLibraryController = this.application.getControllerForElementAndIdentifier(
      mediaLibraryElement,
      'media-library'
    )

    if (mediaLibraryController && mediaLibraryController.existingImgValue) {
      this.removeAssetButtonTarget.click()
    }

    if (!visibleFields.includes('indoorMapField')) {
      this.indoorMapIdFieldTarget.value = ''
    }

    if (!visibleFields.includes('outDoorMapField')) {
      this.outdoorMapIdFieldTarget.value = ''
    }
  }
}
